import { MoreOutlined } from "@ant-design/icons";
import TableUi from "../../../../../../atoms/StaticTable";
import woTable from "../DummyData/woTable.json";
import Filters from "./Filters";
import { Button, Image, TableColumnsType, Tag } from "antd";
import Dropdown from "antd/es/dropdown/dropdown";
import CancelIcon from "../../../../../../atoms/CustomIcons/CancelIcon";

import { useState } from "react";
import ConfirmIcon from "../../../../../../atoms/CustomIcons/ConfirmIcon";

export default function TabWorkOrders() {
  const location = window.location.pathname;
  const pathSegments = location.split("/");

  const companyId = pathSegments[pathSegments.length - 1];
  const [pagination, setPagination] = useState(1);
  const [limit, setLimit] = useState(10);
  const [selectedRowKeys, setSelectedRowKeys] = useState<
    React.Key[] | string[]
  >([]);
  const [archived, isArchived] = useState(false);

  const [filters, setFilters] = useState<{
    type: undefined | string;
    status: undefined | string;
    state: undefined;
    city: undefined;
    last_user_activity: string[] | undefined;
    onboarding_complete_date: string[] | undefined;
    search_text: undefined | string;
  }>({
    type: undefined,
    status: undefined,
    state: undefined,
    city: undefined,
    last_user_activity: undefined,
    onboarding_complete_date: undefined,
    search_text: undefined,
  });

  const items = [
    {
      key: "1",
      label: (
        <Button type="text" icon={<ConfirmIcon />}>
          Change status to...
        </Button>
      ),
    },
    {
      key: "2",
      label: (
        <Button type="text" icon={<CancelIcon />}>
          Cancel work order
        </Button>
      ),
    },
  ];

  const statusClassMap: Record<string, string> = {
    "in progress": "approval_pending_tag",
    open: "grey_tag",
    completed: "accepted_tag",
  };

  const serviceLineClassMap: Record<string, string> = {
    construction: "yellow_tag",
  };

  const columns: TableColumnsType = [
    {
      title: "Work order",
      dataIndex: "work_order",
      width: 220,
      render: (text) => text ?? "-",
      fixed: "left",
      sorter: (a, b) => a?.work_order?.localeCompare(b?.work_order),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 170,
      render: (text) => {
        const cls = statusClassMap[text?.toLowerCase()] || "default_tag";
        return (
          <Tag
            className={cls}
            style={{ textTransform: "capitalize", border: 0 }}
          >
            {text}
          </Tag>
        );
      },
      sorter: (a, b) => a?.status?.localeCompare(b?.status),
    },
    {
      title: "Visits",
      dataIndex: "visits",
      width: 140,
      render: (text) => (text ? text : "-"),
      sorter: (a, b) => a?.visits?.localeCompare(b?.visits),
    },
    {
      title: "Service line",
      dataIndex: "service_line",
      width: 270,
      render: (text) => {
        const cls = serviceLineClassMap[text?.toLowerCase()] || "default_tag";
        return text ? <Tag className={cls}>{text}</Tag> : "-";
      },
      sorter: (a, b) => a?.service_line?.localeCompare(b?.service_line),
    },
    {
      title: "Performed by",
      dataIndex: "performed_by",
      width: 140,
      render: (text) => (text ? text : "-"),
      sorter: (a, b) => a?.performed_by?.localeCompare(b?.performed_by),
    },
    {
      title: "Crew/Subcontractor",
      dataIndex: "crew_sub",
      width: 270,
      render: (text) => (text ? <Tag>{text}</Tag> : "-"),
      sorter: (a, b) => a?.crew_sub?.localeCompare(b?.crew_sub),
    },
    {
      title: "Estimated hrs",
      dataIndex: "estimated_hours",
      width: 150,
      render: (text) => (text ? text : "-"),
      align: "right",
      sorter: (a, b) => a?.estimated_hours - b?.estimated_hours,
      className: "right-aligned-sorter",
    },
    {
      title: "Scheduled hrs",
      dataIndex: "scheduled_hours",
      width: 150,
      render: (text) => (text ? text : "-"),
      align: "right",
      sorter: (a, b) => a?.scheduled_hours - b?.scheduled_hours,
      className: "right-aligned-sorter",
    },
    {
      title: "Actual hrs",
      dataIndex: "actual_hours",
      width: 150,
      render: (text) => (text ? text : "-"),
      align: "right",
      sorter: (a, b) => a?.actual_hours - b?.actual_hours,
    },
    {
      title: "Scheduled dates",
      dataIndex: "scheduled_dates",
      width: 195,
      render: (text) => text ?? "-",
      sorter: (a, b) => a?.scheduled_dates - b?.scheduled_dates,
    },
    {
      title: "Progress",
      dataIndex: "progress",
      width: 195,
      render: (text) => text ?? "-",
      sorter: (a, b) => a?.progress - b?.progress,
    },
    {
      title: "Efficiency",
      dataIndex: "efficiency",
      width: 195,
      render: (text) => text ?? "-",
      sorter: (a, b) => a?.efficiency - b?.efficiency,
    },
    {
      title: "Estimated labor cost",
      dataIndex: "estimated_labor_cost",
      width: 180,
      render: (text) => text ?? "-",
      align: "right",
      sorter: (a, b) => a?.estimated_cost - b?.estimated_cost,
      className: "right-aligned-sorter",
    },
    {
      title: "Actual labor cost",
      dataIndex: "actual_labor_cost",
      width: 180,
      render: (text) => text ?? "-",
      align: "right",
      sorter: (a, b) => a?.actual_cost - b?.actual_cost,
      className: "right-aligned-sorter",
    },
    {
      title: "Estimated material cost",
      dataIndex: "estimated_material_cost",
      width: 220,
      render: (text) => text ?? "-",
      align: "right",
      sorter: (a, b) => a?.estimated_material_cost - b?.estimated_material_cost,
      className: "right-aligned-sorter",
    },
    {
      title: "Actual material cost",
      dataIndex: "actual_material_cost",
      width: 195,
      render: (text) => text ?? "-",
      align: "right",
      sorter: (a, b) => a?.actual_material_cost - b?.actual_material_cost,
      className: "right-aligned-sorter",
    },
    {
      title: "Projected revenue",
      dataIndex: "projected_revenue",
      width: 180,
      render: (text) => text ?? "-",
      align: "right",
      sorter: (a, b) => a?.projected_revenue - b?.projected_revenue,
      className: "right-aligned-sorter",
    },
    {
      title: "Earned revenue",
      dataIndex: "earned_revenue",
      width: 180,
      render: (text) => text ?? "-",
      align: "right",
      sorter: (a, b) => a?.earned_revenue - b?.earned_revenue,
      className: "right-aligned-sorter",
    },
    {
      title: "Date completed",
      dataIndex: "date_completed",
      width: 170,
      render: (text) => text ?? "-",
      align: "right",
    },

    {
      title: <Image src="/images/table/menu-burger.svg" preview={false} />,
      dataIndex: "action",
      width: 52,
      fixed: "right",
      render: (_text, record) => (
        <Dropdown
          menu={{ items }}
          trigger={["click"]}
          placement="bottomRight"
          className="actionButton"
        >
          <Button
            onClick={(e) => {
              console.log("text", record);
              // setTableRecord(record);
              e.stopPropagation();
            }}
            onKeyDown={(e) => e.stopPropagation()}
            style={{
              padding: "6px",
              height: "auto",
              background: "transparent",
            }}
            className="only_icon"
            icon={<MoreOutlined />}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="dashboard-tab">
      <TableUi
        columns={columns}
        data={{
          count: woTable?.length,
          data: woTable || [],
        }}
        filters={
          <Filters
            setFilters={setFilters}
            filters={filters}
            archived={archived}
            isArchived={isArchived}
            companyId={companyId}
          />
        }
        pageSize={limit}
        setPageSize={setLimit}
        setCurrentPage={setPagination}
        currentPage={pagination}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        // onClickRow={onClickRow}
      />
    </div>
  );
}
